import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { LOGO } from "@/lib/brand";
import { Link } from "react-router-dom";
import { Loader2, Disc3, DollarSign, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back!");
      nav("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="dark flex min-h-screen items-center justify-center bg-background p-4">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-2xl border border-border shadow-2xl md:grid-cols-2">
        <div className="relative hidden flex-col justify-between bg-gradient-to-br from-primary/25 via-card to-background p-8 md:flex">
          <img src={LOGO} alt="ARU Music Records" className="h-14 w-auto self-start object-contain" />
          <div>
            <h1 className="font-heading text-3xl font-bold leading-tight">Digital Music Distribution Platform</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Manage releases, royalties, and artist support from one professional dashboard.
            </p>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-3"><Disc3 className="h-4 w-4 text-primary" /><span>Submit and track releases end to end</span></div>
            <div className="flex items-center gap-3"><DollarSign className="h-4 w-4 text-primary" /><span>Transparent royalty reporting and payouts</span></div>
            <div className="flex items-center gap-3"><LifeBuoy className="h-4 w-4 text-primary" /><span>Dedicated artist and label support</span></div>
          </div>
        </div>

        <Card className="rounded-none border-0 bg-card p-8">
          <h2 className="font-heading text-2xl font-bold">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Enter your credentials to continue.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            New here? <Link to="/register" className="text-primary" data-testid="to-register-link">Create an account</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
